import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { MatDialog } from '@angular/material/dialog';
import { CustomContentAdminComponent } from 'src/app/dialogs/custom-content-admin/custom-content-admin.component';
import { MisNotificationsComponent } from 'src/app/dialogs/mis-notifications/mis-notifications.component';
import { CommonService } from 'src/app/services/common.service';
import { ReportsService } from 'src/app/services/reports.service';
import { MisContent, CustomContentService } from 'src/app/services/custom-content.service';
import { TranslateService } from '@ngx-translate/core';
import { PromptWithActionDialogComponent } from 'src/app/dialogs/prompt-with-action/prompt-with-action.component';

export enum AdminRowOperation {
  view = 'view',
  edit = 'edit',
  deactivate = 'deactivate',
  activate = 'activate',
  delete = 'delete',
}

@Component({
  selector: 'admin-row-operations',
  template: `
    <span fxLayout="row" fxLayoutGap="10px">
      <button
        *ngIf="actions?.view"
        mat-button
        vrm-button
        (click)="viewContent()"
        class="grid-text-centred force grid-text action-button"
      >
        {{ 'CUSTOM_CONTENT.view' | translate }}
      </button>

      <button
        *ngIf="actions?.deactivate"
        mat-button
        vrm-button
        (click)="confirmDeactivate()"
        class="grid-text-centred force grid-text action-button"
      >
        {{ 'CUSTOM_CONTENT.deactivate' | translate }}
      </button>

      <button
        *ngIf="actions?.activate"
        mat-button
        vrm-button
        (click)="confirmActivate()"
        class="grid-text-centred force grid-text action-button"
      >
        {{ 'CUSTOM_CONTENT.activate' | translate }}
      </button>

      <button
        *ngIf="actions?.edit"
        mat-button
        vrm-button
        (click)="editContent()"
        class="grid-text-centred force grid-text action-button"
      >
        {{ 'CUSTOM_CONTENT.edit' | translate }}
      </button>

      <button
        *ngIf="actions?.delete"
        mat-button
        (click)="confirmDeletion()"
        class="grid-text-centred force grid-text action-button delete"
      >
        {{ 'CUSTOM_CONTENT.delete' | translate }}
      </button>
    </span>
  `,
  styles: [
    `
      .action-button {
        width: 80px;
        color: white;
        padding-right: 0;
      }
      .action-button.delete {
        background: #cc0000;
      }
    `,
  ],
})
export class AdminRowOperationsComponent implements ICellRendererAngularComp {
  constructor(
    private dialog: MatDialog,
    public reportService: ReportsService,
    public cContentService: CustomContentService,
    private translate: TranslateService,
    public cService: CommonService
  ) {}

  public data: any;
  public actionKeys: Array<string>;
  public rowOperationsMeta: any;
  public actionButtons: any;
  public actions: { view: boolean; edit: boolean; delete: boolean; activate: boolean; deactivate: boolean };
  contentType: MisContent = '';

  agInit(params: { data: any }) {
    this.data = params.data;
    this.actionKeys = this.data.actions.split('|');
    this.rowOperationsMeta = this.reportService.adminRowOperations;

    this.actions = { view: true, edit: false, delete: false, activate: false, deactivate: false };
    for (const actionKey of this.actionKeys) {
      this.actions[actionKey] = this.actions[actionKey] != null;
    }
  }

  viewContent() {
    const content = {
      en: {
        messageID: this.data.messageID,
        contentTitle: this.data.subject,
        contentMessage: this.data.message,
      },
    };

    Object.keys(this.data.variants).forEach((langCode) => {
      content[langCode] = {
        messageID: this.data.variants[langCode].messageID,
        contentTitle: this.data.variants[langCode].subject,
        contentMessage: this.data.variants[langCode].message,
      };
    });

    this.cContentService.notificationViewDialogSettings.data = {
      contents: [content],
      contentType: this.cContentService.currentContentType,
    };

    const dialogRef = this.cContentService.dialog.open(
      MisNotificationsComponent,
      this.cContentService.notificationViewDialogSettings
    );
  }

  editContent() {
    this.cContentService.getContent(this.data.messageID).subscribe({
      next: (misNotification: any) => {
        const dialogSettings = this.cContentService.contentAdminDialogSettings;
        dialogSettings.data = misNotification;
        const dialogRef = this.cContentService.dialog.open(CustomContentAdminComponent, dialogSettings);
        dialogRef.afterClosed().subscribe(() => {
          this.reportService.triggerMisNotificationAdminDialogClosedObservable();
        });
      },
    });
  }

  confirmDeletion() {
    this.cService.promptConfig = {
      title: this.cService.readyTranslations.form.confirm,
      body: this.translate.instant('CUSTOM_CONTENT').deleteConfirmationText,
      actionButtonText: this.cService.readyTranslations.form.confirm,
      cancelButtonText: this.cService.readyTranslations.form.cancel,
    };

    this.dialog
      .open(PromptWithActionDialogComponent)
      .afterClosed()
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.deleteContent();
          }
        },
      });
  }

  confirmDeactivate() {
    this.cService.promptConfig = {
      title: this.cService.readyTranslations.form.confirm,
      body: this.translate.instant('CUSTOM_CONTENT').deactivateConfirmationText,
      actionButtonText: this.cService.readyTranslations.form.confirm,
      cancelButtonText: this.cService.readyTranslations.form.cancel,
    };

    this.dialog
      .open(PromptWithActionDialogComponent)
      .afterClosed()
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.deactivateContent();
          }
        },
      });
  }

  confirmActivate() {
    this.cService.promptConfig = {
      title: this.cService.readyTranslations.form.confirm,
      body: this.translate.instant('CUSTOM_CONTENT').activateConfirmationText,
      actionButtonText: this.cService.readyTranslations.form.confirm,
      cancelButtonText: this.cService.readyTranslations.form.cancel,
    };

    this.dialog
      .open(PromptWithActionDialogComponent)
      .afterClosed()
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.activateContent();
          }
        },
      });
  }

  private deactivateContent() {
    this.cContentService.deactivateContent(this.data.messageID).subscribe({
      next: (response: any) => {
        this.reportService.triggerMisNotificationAdminDialogClosedObservable();
        this.cContentService.updateMenuBadgeCount();
      },
    });
  }

  private activateContent() {
    this.cContentService.activateContent(this.data.messageID).subscribe({
      next: (response: any) => {
        this.reportService.triggerMisNotificationAdminDialogClosedObservable();
        this.cContentService.updateMenuBadgeCount();
      },
    });
  }

  private deleteContent() {
    this.cContentService.deleteContent(this.data.messageID).subscribe({
      next: (response: any) => {
        this.reportService.triggerRowsDeleted(this.data);
        this.cContentService.updateMenuBadgeCount();
      },
    });
  }

  refresh() {
    return false;
  }
}
